// @react
import React, { useState, ReactElement, useEffect, useContext } from 'react'
// @typings
import {
  GetUserQuery,
  UserToggleMemberDirectoryPrivacyMutation,
  UserToggleMemberDirectoryPrivacyMutationVariables,
} from 'lib/graphql/graphqlTypes'
import { SnackbarTypes } from '@bit/atd.web.snackbar'
// @graphql
import { useMutation } from '@apollo/client'
import { USER_TOGGLE_MEMBER_DIRECTORY_PRIVACY } from 'graphql/mutations/user.mutation'
import { GET_USER } from 'graphql/queries/user.query'
// @libraries
import Sprintf from 'sprintf-js'
// @commons
import strings from 'lib/constants/strings'
// @context
import { SnackbarContext } from 'modules/common/context/snackbar'
// @components
import Toggle from '@bit/atd.web.toggle'
// @module components

/**
 * 5.0.1.1 Profile - Personal Information - Visibility
 * UX: https://www.figma.com/file/jCo0D2EYkVbuBX1TjR1enA/ATD-MyATD-Desktop-2020?node-id=3123%3A16072
 *
 * endpoint: user/profile
 * response: UserProfileDTO
 *
 * fields:
 * publicProfile
 * amemberDirectoryListing
 *
 */

const vsprintf = Sprintf.vsprintf

type PropsType = {
  data?: GetUserQuery
  isLoading: boolean
}

const MemberVisibility = ({ data, isLoading }: PropsType): ReactElement => {
  /****************************************
   * STATE
   *****************************************/

  const [publicProfile, setPublicProfile] = useState<boolean>(false)
  const [listingProfile, setListingProfile] = useState<boolean>(false)

  /****************************************
   * HOOKS
   *****************************************/

  const snackbar = useContext(SnackbarContext)
  const visibility = data?.user?.visibility
  useEffect(() => {
    setPublicProfile(!!visibility?.publicProfile)
    setListingProfile(!!visibility?.memberDirectoryListing)
  }, [isLoading, visibility])

  /****************************************
   * GQL HOOKS
   *****************************************/

  const [toggleMemberDirectoryPrivacy] = useMutation<
    UserToggleMemberDirectoryPrivacyMutation,
    UserToggleMemberDirectoryPrivacyMutationVariables
  >(USER_TOGGLE_MEMBER_DIRECTORY_PRIVACY, {
    update(cache) {
      cache.writeQuery({
        query: GET_USER,
        data: {
          user: {
            ...data?.user,
            visibility: {
              ...data?.user?.visibility,
              memberDirectoryListing: !listingProfile,
            },
          },
        },
      })
    },
  })

  /****************************************
   * FUNCTIONS
   *****************************************/
  const handleListingToggle = async () => {
    const { errors: mutationErrors } = await toggleMemberDirectoryPrivacy({
      variables: {
        input: !publicProfile,
      },
    })

    if (!mutationErrors) {
      snackbar.setMessage({
        type: SnackbarTypes.SUCCESS,
        message: vsprintf(strings.SUCCESS.GENERIC_VARIABLE_UPDATE_SUCCESS, [
          'Privacy',
        ]),
        show: true,
      })
      setListingProfile(!listingProfile)
    } else {
      snackbar.setMessage({
        type: SnackbarTypes.ERROR,
        message: strings.ERRORS.GENERIC_UPDATE_ERROR,
        show: true,
      })
      console.log('mutation error', mutationErrors)
    }
  }

  /****************************************
   * RENDER
   *****************************************/

  return (
    <Toggle
      name="listingProfile"
      label={strings.PROFILE.VISIBILITY.INCLUDE_IN_MEMBER_DIR}
      labelPlacement="right"
      size="medium"
      disabled={isLoading}
      checked={listingProfile}
      onChange={handleListingToggle}
      helperText={
        <span>{strings.PROFILE.VISIBILITY.INCLUDE_IN_MEMBER_DIR_HELP}</span>
      }
    />
  )
}

export default MemberVisibility
